import React, { useContext, useState } from 'react'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { Menu, MenuItem } from '@mui/material'
import { useDispatch } from 'react-redux'
import useModals from 'src/Hooks/useModals'
import { EMobilityChargingCardsDataModel } from './models/e-mobility-charging-cards-models'
import { EMobilityAccessRightsContext } from 'src/Libs/system-settings/provider/EMobilityAccessRightsContext'

interface EMobilityChargingCardTableMenuProps {
  isOpen: boolean
  anchorEl: null | HTMLElement
  handleClose: () => void
  data: EMobilityChargingCardsDataModel
  storageId: string
}

const EMobilityChargingCardTableMenu: React.FC<EMobilityChargingCardTableMenuProps> = ({
  isOpen,
  anchorEl,
  handleClose,
  data,
  storageId,
}): React.ReactElement => {
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { openEmobilityChargingCardEditor, openConfirmationModal, openEmobilityExportChargingCardEditor, openChargingSessionsOverview } = useModals()

  const { t } = useCustomTranslation()
  const dispatch = useDispatch()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { hasEMobilityWritingRights } = useContext(EMobilityAccessRightsContext)

  const handleDeleteChargingCard = (): void => {
    openConfirmationModal({
      title: t('emobility.chargingCards.delete.title', { chargingCardName: data.name, rfid: data.rfid }),
      text: t('emobility.chargingCards.delete.content'),
      buttonText: [t('emobility.chargingCards.delete.no'), t('emobility.chargingCards.delete.yes')],
      onConfirm: async onClose => {
        setIsLoading(true)
        try {
          await authFetch({
            endpoint: process.env.REACT_APP_EMOBILITY_ENDPOINT,
            path: `e-mobility/${storageId}/cards/${data.rfid}`,
            method: 'DELETE',
          })
          enqueueSuccessStack(t('emobility.chargingCards.delete.successMessage'))
          dispatch(refreshApiView())
          onClose()
        } catch {
          enqueueErrorStack(t('emobility.chargingCards.delete.errorMessage'))
        } finally {
          setIsLoading(false)
        }
      },
      isLoading: isLoading,
    })
  }

  return (
    <Menu anchorEl={anchorEl} open={isOpen} onClose={handleClose}>
      <MenuItem
        onClick={() => {
          handleClose()
          openEmobilityChargingCardEditor({ data: data, serialNo: storageId })
        }}
        disabled={!hasEMobilityWritingRights}
      >
        {t('emobility.chargingCards.editChargingCard')}
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleClose()
          handleDeleteChargingCard()
        }}
        disabled={!hasEMobilityWritingRights}
      >
        {t('emobility.chargingCards.deleteChargingCard')}
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleClose()
          openEmobilityExportChargingCardEditor({
            rfid: data.rfid,
            vehicleName: data.vehicleName || '',
            chargingCard: data.name || '',
            storageId: storageId,
          })
        }}
      >
        {t('emobility.chargingCards.exportLoads')}
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleClose()
          openChargingSessionsOverview({ chargingCard: data.name, rfid: data.rfid, storageId })
        }}
      >
        {t('emobility.chargingCards.viewChargingSessions')}
      </MenuItem>
    </Menu>
  )
}

export default EMobilityChargingCardTableMenu
